import { useState, useEffect, useCallback } from 'react';
import {
  GxSubscription,
  getSubscription,
  subscribe as apiSubscribe,
  unsubscribe as apiUnsubscribe,
} from '../api/guestExperience';

interface UseGxSubscriptionResult {
  subscription: GxSubscription | null;
  isLoading: boolean;
  subscribe: (zoneFilters?: string[]) => Promise<void>;
  unsubscribe: () => Promise<void>;
}

export function useGxSubscription(eventId: string): UseGxSubscriptionResult {
  const [subscription, setSubscription] = useState<GxSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getSubscription(eventId);
      setSubscription(data);
    } catch {
      // No subscription found or error — treat as null
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const subscribe = useCallback(
    async (zoneFilters?: string[]) => {
      const data = await apiSubscribe(eventId, zoneFilters);
      setSubscription(data);
    },
    [eventId],
  );

  const unsubscribe = useCallback(async () => {
    await apiUnsubscribe(eventId);
    setSubscription(null);
  }, [eventId]);

  return { subscription, isLoading, subscribe, unsubscribe };
}
